/** @jsxImportSource @emotion/react */
import { ChangeEvent } from 'react'
import { Field } from 'react-final-form'
import { FieldArrayRenderProps } from 'react-final-form-arrays'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormHelperText from '@mui/material/FormHelperText'
import { findOneQuestion_findOneQuestion_answer as Answer } from '__generatedTypes__/findOneQuestion'
import { EditorField } from 'components/Editor'
import { InputField, NumberInputField } from 'components/Input'
import TableButtons from 'components/TableButtons/TableButtons'
import { ReactComponent as TrashIcon } from 'assets/icons/trash.svg'
import { getAnswerLetterLabel } from 'utils/getAnswerLetterLabel'
import { validateRequiredNumberBetween } from 'utils/validation'
import * as commonClasses from '../commonStyles'
import { OptionInputKind } from '../questionTypeConfig'
import * as classes from './styles'

export type AnswersFieldArrayProps = {
  className?: string
  fields: FieldArrayRenderProps<Partial<Answer>, HTMLDivElement>['fields']
  meta: FieldArrayRenderProps<Partial<Answer>, HTMLElement>['meta']
  onAnswerChange?: () => void
  isMultiple?: boolean
  showPoints?: boolean
  optionInput?: OptionInputKind
}

export const MIN_ANSWERS_COUNT = 2

export const EMPTY_ANSWER = { textAsHtml: '', isCorrect: false }

export const AnswersFieldArray = ({
  className,
  fields,
  meta,
  onAnswerChange,
  isMultiple,
  showPoints,
  optionInput = 'html',
}: AnswersFieldArrayProps) => {
  const handleCorrectAnswerChange =
    (onFieldChange: (event: ChangeEvent<HTMLInputElement>) => void, fieldIndex: number) =>
    (event: ChangeEvent<HTMLInputElement>) => {
      onFieldChange(event)
      const { checked } = event.target

      // If an answer is marked as correct the app needs to unmark previously selected answer
      if (!isMultiple && checked) {
        fields.forEach((_, index) => {
          if (index !== fieldIndex) {
            fields.update(index, { ...fields.value[index], isCorrect: false })
          }
        })
      }
      onAnswerChange?.()
    }

  const error = Array.isArray(meta.error) ? meta.error[0] : meta.error

  return (
    <div className={className}>
      <div css={classes.wrapper}>
        {fields.length ? (
          fields.map((field, index) => (
            <div key={field} css={classes.item}>
              <div css={classes.itemHeader}>
                <Field name={`${field}.isCorrect`} type="checkbox">
                  {({ input }) => (
                    <FormControlLabel
                      label={`Correct Answer: ${getAnswerLetterLabel(index)}.`}
                      control={<Checkbox {...input} onChange={handleCorrectAnswerChange(input.onChange, index)} />}
                      css={classes.checkbox}
                    />
                  )}
                </Field>
                <div css={classes.pointsWrapper}>
                  {showPoints && (
                    <>
                      <div>Points: {fields.value[index].isCorrect ? '+' : '-'}</div>
                      <NumberInputField
                        name={`${field}.point`}
                        validate={(value) => validateRequiredNumberBetween(value, 0, 1)}
                        onChange={() => onAnswerChange?.()}
                        placeholder="Enter Points"
                        size="small"
                        css={classes.pointsInput}
                        decimal
                      />
                    </>
                  )}
                  <Button
                    variant="outlined"
                    color="red"
                    size="small"
                    onClick={() => fields.remove(index)}
                    disabled={(fields.length ?? 0) <= MIN_ANSWERS_COUNT}
                    css={classes.removeButton}
                  >
                    <TrashIcon />
                  </Button>
                </div>
              </div>

              {optionInput === 'plain' ? (
                <InputField name={`${field}.textAsHtml`} placeholder="Enter Option" fullWidth />
              ) : (
                <EditorField
                  type="option"
                  name={`${field}.textAsHtml`}
                  placeholder="Enter Option"
                  css={commonClasses.editor}
                />
              )}
            </div>
          ))
        ) : (
          <div css={classes.empty}>No options added</div>
        )}
      </div>

      <TableButtons itemName="New Option" onAddItem={() => fields.push(EMPTY_ANSWER)} />
      {Boolean(meta.touched && error) && <FormHelperText error>{String(error)}</FormHelperText>}
    </div>
  )
}
